import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '@/utils/supabase';
import { initializeSecurityCleanup } from '@/utils/securityCleanup';
import { SessionRecovery } from '@/utils/sessionRecovery';
import { isSessionlessPublicRoute, isAuthAdjacentRoute } from '@/utils/routeUtils';
import { createComponentLogger } from '@/utils/logger';
import { performanceTracker } from '@/utils/performanceTracker';
import { GmailService } from '@/services/gmailService';
import {
  hasGoogleProvider,
  isGoogleOAuthSession,
  shouldStoreGmailTokens
} from '@/contexts/gmailTokenStorageUtils';
import { isEquivalentExtendedUser, isEquivalentSession } from '@/contexts/authStateUtils';
import { AuthContext } from '@/contexts/authContextDefinition';
import type { ExtendedUser } from '@/types/auth';
// import { UserService } from '@/services/userService';

interface AuthProviderProps {
  children: React.ReactNode;
}

const log = createComponentLogger('AuthProvider', 'authentication');

// Cache TTL: 5 minutes for profile existence checks
const PROFILE_CACHE_TTL = 5 * 60 * 1000;

function clearSupabaseBrowserStorage(): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith('sb-')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

// Full profile data cached per user (roles, names, employment_status)
interface CachedProfile {
  id?: string;
  email?: string | null;
  roles: string[] | null;
  organization_id: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  employment_status?: string | null;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Auth event cascade prevention and performance optimization
  const processedEvents = useRef<Map<string, number>>(new Map());
  const profileCheckCache = useRef<Map<string, {
    exists: boolean;
    timestamp: number;
    profile?: CachedProfile;
  }>>(new Map());
  const activeProfileRequests = useRef<Map<string, Promise<boolean>>>(new Map());
  const gmailTokenAttempts = useRef<Set<string>>(new Set());
  const gmailTokenStorageAttempts = useRef<Set<string>>(new Set());
  const authStateDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const authStateSequenceRef = useRef(0);
  const signOutInFlightRef = useRef<Promise<void> | null>(null);

  const clearClientAuthState = useCallback(() => {
    if (authStateDebounceRef.current) {
      clearTimeout(authStateDebounceRef.current);
      authStateDebounceRef.current = null;
    }

    profileCheckCache.current.clear();
    activeProfileRequests.current.clear();
    gmailTokenAttempts.current.clear();
    gmailTokenStorageAttempts.current.clear();
    processedEvents.current.clear();
    GmailService.clearCache();
    localStorage.removeItem('supabase_session');
    sessionStorage.removeItem('impersonated_user_id');
    localStorage.removeItem('dev_impersonated_user');
    localStorage.removeItem('dev_user_role');

    // Protected React state must disappear before Supabase removes the token.
    // Otherwise mounted children can start protected reads as the anon role.
    setSession(null);
    setUser(null);
    setLoading(false);
  }, []);

  // Gmail tokens are now handled directly by Edge Functions
  // No need to extract them separately

  // Helper function to ensure user profile exists with caching and deduplication
  const ensureUserProfile = useCallback(async (user: User) => {
    const userId = user.id;
    const userEmail = user.email;
    
    // Check cache first
    const cached = profileCheckCache.current.get(userId);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < PROFILE_CACHE_TTL) {
      if (cached.exists) {
        return true;
      }
    }
    
    // Check if there's already an active request for this user
    const activeRequest = activeProfileRequests.current.get(userId);
    if (activeRequest) {
      return await activeRequest;
    }
    
    // Create and cache the request promise to prevent duplicate requests
    const profileRequest = async (): Promise<boolean> => {
      try {
        const { data: profile, error } = await supabase
          .from('users')
          .select('id, organization_id, roles, first_name, last_name, full_name')
          .eq('id', user.id)
          .single();
          
        const exists = !!profile;
        
        // Cache the result
        profileCheckCache.current.set(userId, { exists, timestamp: now });
        
        // Clean up active request
        activeProfileRequests.current.delete(userId);

        if (error && error.code === 'PGRST116') {
          // Profile doesn't exist, create it
        
          // Get pending users organization
          const { data: org } = await supabase
            .from('organizations')
            .select('id')
            .eq('subdomain', 'pending-users')
            .single();

          if (org) {
            // Create profile with pending status for admin approval
            const { error: createError } = await supabase
              .from('users')
              .insert({
                id: user.id,
                email: user.email,
                organization_id: org.id,
                first_name: user.user_metadata?.given_name || user.user_metadata?.first_name || '',
                last_name: user.user_metadata?.family_name || user.user_metadata?.last_name || '',
                full_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email,
                roles: null,
                employment_status: 'pending'
              });

            if (createError) {
              log.error('Profile creation failed', { userEmail }, createError);
              return false;
            }
            log.debug('Profile created successfully', { userEmail });
            // Update cache with successful creation
            profileCheckCache.current.set(userId, { exists: true, timestamp: now });
            return true;
          }
          return false;
        } else if (!profile?.organization_id) {
          // Profile exists but no organization, update it with pending status
          const { data: org } = await supabase
            .from('organizations')
            .select('id')
            .eq('subdomain', 'pending-users')
            .single();

          if (org) {
            await supabase
              .from('users')
              .update({
                organization_id: org.id,
                roles: null,
                employment_status: 'pending'
              })
              .eq('id', user.id);
          }
        }
        
        return exists;
        
      } catch (err) {
        log.error('Profile ensure operation failed', { userId, userEmail }, err as Error);
        // Clean up active request on error
        activeProfileRequests.current.delete(userId);
        return false;
      }
    };
    
    // Store and execute the request
    activeProfileRequests.current.set(userId, profileRequest());
    return await activeProfileRequests.current.get(userId)!;
  }, []);

  // NEW CONSOLIDATED FUNCTION: Fetches and caches complete user profile in a single query
  // This replaces both extendUserWithDatabaseFields and ensureUserProfile
  const loadCompleteUserProfile = useCallback(async (authUser: User): Promise<User | null> => {
    const userId = authUser.id;
    const userEmail = authUser.email;

    // Auth events and React effects can race with SIGNED_OUT. Re-check the
    // browser client's current session before either using cached protected
    // profile data or issuing a users-table query.
    const { data: { session: liveSession }, error: sessionError } =
      await supabase.auth.getSession();
    if (sessionError || !liveSession || liveSession.user.id !== userId) {
      log.debug('Skipping profile load because the session is no longer active', {
        userId
      });
      return null;
    }

    // Check cache first for complete profile
    const cached = profileCheckCache.current.get(userId);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < PROFILE_CACHE_TTL && cached.profile) {
      // Apply cached profile to user object
      const extendedUser = authUser as ExtendedUser;
      extendedUser.roles = cached.profile.roles || [];
      extendedUser.organization_id = cached.profile.organization_id || '';
      extendedUser.firstName = cached.profile.first_name || '';
      extendedUser.lastName = cached.profile.last_name || '';
      extendedUser.fullName = cached.profile.full_name || authUser.email || '';
      extendedUser.employment_status = cached.profile.employment_status;

      log.debug('Using cached profile data', { userId, cacheAge: now - cached.timestamp });
      return authUser;
    }

    try {
      // Single query to get ALL user data we need
      const { data: profile, error } = await supabase
        .from('users')
        .select('id, roles, organization_id, first_name, last_name, full_name, employment_status')
        .eq('id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        // Profile doesn't exist, create it
        log.info('Creating new user profile', { userId, userEmail });

        // Get pending users organization
        const { data: org } = await supabase
          .from('organizations')
          .select('id')
          .eq('subdomain', 'pending-users')
          .single();

        if (org) {
          // Create profile with pending status for admin approval
          const newProfile = {
            id: authUser.id,
            email: authUser.email,
            organization_id: org.id,
            first_name: authUser.user_metadata?.given_name || authUser.user_metadata?.first_name || '',
            last_name: authUser.user_metadata?.family_name || authUser.user_metadata?.last_name || '',
            full_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email,
            roles: null,
            employment_status: 'pending'
          };

          const { error: createError } = await supabase
            .from('users')
            .insert(newProfile);

          if (createError) {
            log.error('Profile creation failed', { userEmail }, createError);
            // Set minimal defaults on error
            const extendedUser = authUser as ExtendedUser;
            extendedUser.roles = [];
            extendedUser.organization_id = '';
            extendedUser.firstName = '';
            extendedUser.lastName = '';
            extendedUser.fullName = authUser.email || '';
            return authUser;
          }

          // Use the created profile data
          const extendedUser = authUser as ExtendedUser;
          extendedUser.roles = newProfile.roles || [];
          extendedUser.organization_id = newProfile.organization_id;
          extendedUser.firstName = newProfile.first_name;
          extendedUser.lastName = newProfile.last_name;
          extendedUser.fullName = newProfile.full_name;
          extendedUser.employment_status = newProfile.employment_status;

          // Cache the new profile
          profileCheckCache.current.set(userId, {
            exists: true,
            timestamp: now,
            profile: newProfile
          });

          log.debug('Profile created and cached', { userId });
          return authUser;
        }
      } else if (profile) {
        // Profile exists, extend user and cache
        const extendedUser = authUser as ExtendedUser;
        extendedUser.roles = profile.roles || [];
        extendedUser.organization_id = profile.organization_id || '';
        extendedUser.firstName = profile.first_name || '';
        extendedUser.lastName = profile.last_name || '';
        extendedUser.fullName = profile.full_name || authUser.email || '';
        extendedUser.employment_status = profile.employment_status;

        // Cache the complete profile
        profileCheckCache.current.set(userId, {
          exists: true,
          timestamp: now,
          profile
        });

        log.debug('Profile loaded and cached', {
          userId,
          organizationId: profile.organization_id,
          roles: profile.roles,
          fullName: profile.full_name,
          employmentStatus: profile.employment_status
        });

        // Handle missing organization (migration case)
        if (!profile.organization_id) {
          const { data: org } = await supabase
            .from('organizations')
            .select('id')
            .eq('subdomain', 'pending-users')
            .single();

          if (org) {
            await supabase
              .from('users')
              .update({
                organization_id: org.id,
                roles: null,
                employment_status: 'pending'
              })
              .eq('id', userId);

            // Update cache with new org
            extendedUser.organization_id = org.id;
            extendedUser.employment_status = 'pending';

            const updatedCache = profileCheckCache.current.get(userId);
            if (updatedCache?.profile) {
              updatedCache.profile.organization_id = org.id;
              updatedCache.profile.employment_status = 'pending';
            }
          }
        }

        return authUser;
      }

      // No profile and couldn't create one - use defaults
      log.warn('No profile found and could not create', { userId });
      const extendedUser = authUser as ExtendedUser;
      extendedUser.roles = [];
      extendedUser.organization_id = '';
      extendedUser.firstName = '';
      extendedUser.lastName = '';
      extendedUser.fullName = authUser.email || '';
      return authUser;

    } catch (error) {
      log.error('Failed to load user profile', { userId }, error as Error);
      // Set defaults on error
      const extendedUser = authUser as ExtendedUser;
      extendedUser.roles = [];
      extendedUser.organization_id = '';
      extendedUser.firstName = '';
      extendedUser.lastName = '';
      extendedUser.fullName = authUser.email || '';
      return authUser;
    }
  }, []);

  // Event deduplication utility
  const shouldProcessEvent = useCallback((event: string, userId: string) => {
    const eventKey = `${event}:${userId}`;
    const now = Date.now();
    const lastProcessed = processedEvents.current.get(eventKey);
    
    // Allow processing if not processed recently (within 100ms)
    if (!lastProcessed || now - lastProcessed > 100) {
      processedEvents.current.set(eventKey, now);
      return true;
    }
    
    // Skip duplicate event processing silently
    return false;
  }, []);

  // Profile check with caching
  const _ensureUserProfileCached = useCallback(async (user: User) => {
    if (profileCheckCache.current.has(user.id)) {
      log.debug('Profile check cached', { userEmail: user.email });
      return;
    }
    
    await ensureUserProfile(user);
    profileCheckCache.current.set(user.id, { exists: true, timestamp: Date.now() });
  }, [ensureUserProfile]);

  // Gmail token check (tokens are now stored during OAuth callback)
  const checkGmailConnection = useCallback(async (session: Session | null = null) => {
    // Get current session if not provided
    const sessionToCheck = session || (await supabase.auth.getSession()).data.session;
    
    if (!sessionToCheck) {
      log.debug('No session available for Gmail check');
      return;
    }
    
    const sessionKey = `${sessionToCheck.user.id}:${sessionToCheck.access_token.substring(0, 10)}`;
    
    if (gmailTokenAttempts.current.has(sessionKey)) {
      log.debug('Gmail connection already checked for this session');
      return;
    }
    
    gmailTokenAttempts.current.add(sessionKey);
    
    if (!hasGoogleProvider(sessionToCheck.user?.app_metadata)) {
      return;
    }
    
    try {
      const { GmailService } = await import('@/services/gmailService');
      const connected = await GmailService.checkGmailConnection();
      
      if (connected) {
        log.debug('Gmail connection verified');
      } else {
        log.info('Gmail tokens not found - user may need to reconnect');
      }
    } catch (err) {
      log.error('Error checking Gmail connection', undefined, err as Error);
    }
  }, []);

  // Listen for Gmail tokens stored event
  useEffect(() => {
    const handleGmailTokensStored = (event: CustomEvent) => {
      log.debug('Gmail tokens stored event received', { userId: event.detail?.userId });
      // Check Gmail connection after tokens are stored
      checkGmailConnection().catch(err => {
        log.error('Gmail connection check failed after token storage', undefined, err);
      });
    };

    window.addEventListener('gmail-tokens-stored', handleGmailTokensStored as EventListener);
    
    return () => {
      window.removeEventListener('gmail-tokens-stored', handleGmailTokensStored as EventListener);
    };
  }, [checkGmailConnection]);

  useEffect(() => {
    // Skip auth setup if Supabase is not configured
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    // Sessionless public routes (borrower portals, previews) don't need
    // employee auth — clear stale sb-* keys to stop refresh attempts.
    // Auth-adjacent routes (/, /auth/redirect, /auth/callback*) MUST NOT
    // wipe tokens: they either need the session (email deep links) or are
    // actively writing it (OAuth callbacks).
    const currentPath = window.location.pathname;
    if (isSessionlessPublicRoute(currentPath)) {
      log.debug('On sessionless public route, clearing any stale auth tokens', { path: currentPath });

      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-')) {
          keysToRemove.push(key);
        }
      }

      if (keysToRemove.length > 0) {
        log.debug('Removing stale auth keys from sessionless public route', { count: keysToRemove.length });
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }
    }

    // One-time cleanup for auth cache issues. Skip on auth-adjacent routes so
    // an email deep link or OAuth callback can't be wiped mid-flow; users with
    // a stale version pick up the cleanup on the next fresh load of any other
    // route.
    const AUTH_VERSION = 'v4-public-route-fix';
    const currentVersion = localStorage.getItem('auth-version');
    if (currentVersion !== AUTH_VERSION && !isAuthAdjacentRoute(currentPath)) {
      log.debug('Performing one-time auth cleanup');
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('lendwise-auth') || key.startsWith('sb-'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      sessionStorage.clear();

      localStorage.setItem('auth-version', AUTH_VERSION);
      log.debug('Auth cleanup completed');
    }

    let mounted = true;

    // Get initial session with improved recovery
    const initializeAuth = async () => {
      const initializationSequence = authStateSequenceRef.current;
      performanceTracker.mark('auth_init_start');
      const authTimer = log.startTimer('auth_initialization');
      
      // Skip auth initialization only on sessionless public routes (borrower
      // portals, previews) — those never need an employee session.
      //
      // Auth-adjacent routes (/, /auth/redirect, /auth/callback*) DO run init.
      // On /auth/callback this gives AuthContext a direct path to restore the
      // session that Supabase's `detectSessionInUrl: true` has written to
      // localStorage, in case the `onAuthStateChange` listener missed the
      // event during the createClient → React-mount window.
      // `OAuthCallback` then owns the consume-once redirect navigation.
      const currentPath = window.location.pathname;
      if (isSessionlessPublicRoute(currentPath)) {
        log.debug('Skipping auth initialization for sessionless public route', { path: currentPath });
        setLoading(false);
        return;
      }
      
      // Declared outside try so the finally-block timer log sees the resolved
      // value (not the outer React state, which is always null at mount).
      let session: Session | null = null;
      try {
        // Use the new session recovery mechanism
        session = await SessionRecovery.getValidSession();

        if (!mounted || authStateSequenceRef.current !== initializationSequence) {
          return;
        }

        if (mounted && session?.user) {
          // SINGLE QUERY: Load complete user profile with all fields
          const extendedUser = await loadCompleteUserProfile(session.user);

          if (!mounted || authStateSequenceRef.current !== initializationSequence) {
            return;
          }

          if (!extendedUser) {
            setSession(null);
            setUser(null);
            setLoading(false);
            return;
          }

          // Set session immediately to avoid delays
          setSession(session);
          setUser(extendedUser);

          // Store session info for Gmail service
          localStorage.setItem('supabase_session', JSON.stringify(session));

          // Clear Gmail cache on new session
          GmailService.clearCache();

          // Profile already created/ensured by loadCompleteUserProfile - no need for ensureUserProfile

          // Store current path to restore after auth completes
          const currentPath = window.location.pathname;
          if (currentPath !== '/' && currentPath !== '/internal') {
            sessionStorage.setItem('auth-redirect-path', currentPath);
          }

          setLoading(false);
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
          }

          // No need for background profile check - already handled by loadCompleteUserProfile
        } else if (mounted) {
          setSession(null);
          setUser(null);
          setLoading(false);
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
          }
        }
      } catch (error) {
        log.error('Auth initialization failed', undefined, error as Error);
        if (mounted && authStateSequenceRef.current === initializationSequence) {
          setLoading(false);
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
          }
        }
      } finally {
        log.endTimer(authTimer, { hasSession: !!session });
        performanceTracker.mark('auth_init_complete');
      }
    };

    // Extended timeout with performance monitoring
    let loadingTimeout: NodeJS.Timeout | undefined;
    
    // Only set timeout if not on login page or public routes
    const isPublicPath = window.location.pathname.startsWith('/profiles/') || 
                        window.location.pathname.startsWith('/public/');
    
    if (window.location.pathname !== '/' && !isPublicPath) {
      // Force-complete at ~10s; setLoading(false) is a no-op when auth has
      // already finished, so no state read is needed here.
      loadingTimeout = setTimeout(() => {
        if (mounted) {
          // Silently handle timeout - no logging needed
          setTimeout(() => {
            if (mounted) {
              setLoading(false); // Prevent infinite loading state
            }
          }, 5000);
        }
      }, 5000); // Initial warning at 5s, force completion at 10s
    }

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      log.debug('onAuthStateChange event', {
        event: _event,
        hasSession: !!session
      });

      // Supabase removes the browser session before emitting SIGNED_OUT.
      // Clear protected state synchronously so no mounted child can issue a
      // users-table request with the now-anonymous client.
      if (_event === 'SIGNED_OUT') {
        authStateSequenceRef.current += 1;
        clearClientAuthState();
        initializeSecurityCleanup();
        window.dispatchEvent(new CustomEvent('auth-state-changed', {
          detail: { event: _event, user: null }
        }));
        return;
      }

      // Domain policy gate — reject disallowed Google accounts immediately,
      // before any token storage or profile work runs.
      if (_event === 'SIGNED_IN' &&
          session &&
          isGoogleOAuthSession(session) &&
          (!session.user.email || !session.user.email.endsWith('@lendwisemtg.com'))) {
        log.warn('Rejecting non-allowed Google domain before token storage', {
          email: session.user.email
        });
        authStateSequenceRef.current += 1;
        clearClientAuthState();
        // Auth callbacks run under the Supabase client lock. Defer sign-out
        // until the synchronous callback has returned to avoid lock re-entry.
        authStateDebounceRef.current = setTimeout(() => {
          authStateDebounceRef.current = null;
          void supabase.auth.signOut().then(({ error }) => {
            if (error) {
              log.error('Failed to sign out disallowed Google account', undefined, error);
            }
          }).catch((error) => {
            log.error(
              'Exception signing out disallowed Google account',
              undefined,
              error as Error
            );
          });
        }, 0);
        return;
      }

      // Gmail token storage runs BEFORE the event dedup gate.
      // shouldProcessEvent may drop a duplicate SIGNED_IN that carries provider_token,
      // so token storage must be independent. It has its own dedup via gmailTokenStorageAttempts.
      if (_event === 'SIGNED_IN' && session) {
        const { store, dedupeKey } = shouldStoreGmailTokens(
          _event, session, gmailTokenStorageAttempts.current
        );

        if (store && dedupeKey) {
          gmailTokenStorageAttempts.current.add(dedupeKey);
          log.info('Google SIGNED_IN with provider token, storing Gmail tokens');

          supabase.functions.invoke('store-gmail-tokens', {
            body: {
              provider_token: session.provider_token,
              provider_refresh_token: session.provider_refresh_token || null
            },
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          }).then(({ error: storeError }) => {
            if (storeError) {
              log.error('Failed to store Gmail tokens on login', { error: storeError.message });
              gmailTokenStorageAttempts.current.delete(dedupeKey);
            } else {
              log.info('Gmail tokens stored successfully on login');
              GmailService.clearCache();
              window.dispatchEvent(new CustomEvent('gmail-tokens-stored', {
                detail: { userId: session.user?.id }
              }));
            }
          }).catch(error => {
            log.error('Error storing Gmail tokens on login', {
              error: error instanceof Error ? error.message : String(error)
            });
            gmailTokenStorageAttempts.current.delete(dedupeKey);
          });
        } else if (!session.provider_token && isGoogleOAuthSession(session)) {
          log.warn('Google SIGNED_IN but no provider_token in session', {
            userId: session.user?.id,
            email: session.user?.email
          });
        }
      }

      // Skip processing duplicate events for profile/state updates
      if (session?.user && !shouldProcessEvent(_event, session.user.id)) {
        return;
      }

      const eventSequence = authStateSequenceRef.current + 1;
      authStateSequenceRef.current = eventSequence;

      // Clear any existing debounce
      if (authStateDebounceRef.current) {
        clearTimeout(authStateDebounceRef.current);
      }
      
      // Debounce auth state changes to prevent cascade
      authStateDebounceRef.current = setTimeout(async () => {
        authStateDebounceRef.current = null;
        if (!mounted || authStateSequenceRef.current !== eventSequence) {
          return;
        }
        
        // Safety-net domain check (primary check is at the top of onAuthStateChange,
        // before Gmail token storage). Kept for non-SIGNED_IN event types or edge cases.
        if (isGoogleOAuthSession(session) &&
            session?.user?.email &&
            !session.user.email.endsWith('@lendwisemtg.com')) {
          // Sign out non-domain Google OAuth users
          authStateSequenceRef.current += 1;
          clearClientAuthState();
          const { error } = await supabase.auth.signOut();
          if (error) {
            log.error(
              'Failed safety-net sign-out for disallowed Google account',
              undefined,
              error
            );
          }
          return;
        }
        
        if (mounted && session?.user) {
          // SINGLE QUERY: Load complete user profile with all fields (uses cache after first load)
          const extendedUser = await loadCompleteUserProfile(session.user);

          if (!mounted || authStateSequenceRef.current !== eventSequence) {
            return;
          }

          if (!extendedUser) {
            clearClientAuthState();
            return;
          }

          // SIGNED_IN re-broadcasts (e.g. from same-origin template-preview
          // iframes) and routine TOKEN_REFRESHED must not churn user/session
          // identity when nothing material changed — identity churn re-runs
          // every [user]/[session] effect in the app. USER_UPDATED and other
          // events keep unconditional sets.
          if (_event === 'SIGNED_IN' || _event === 'TOKEN_REFRESHED') {
            setSession(prev => (isEquivalentSession(prev, session) ? prev : session));
            setUser(prev => (isEquivalentExtendedUser(prev, extendedUser) ? prev : extendedUser));
          } else {
            setSession(session);
            setUser(extendedUser);
          }
          setLoading(false);

          // Profile already created/ensured by loadCompleteUserProfile - no need for additional checks

          // Clear Gmail cache and dispatch auth change event
          if (_event === 'SIGNED_IN') {
            GmailService.clearCache();
            window.dispatchEvent(new CustomEvent('auth-state-changed', {
              detail: { event: _event, user: session?.user }
            }));
          }
        } else if (mounted) {
          setSession(null);
          setUser(null);
          setLoading(false); // Critical: Clear loading state
          log.debug('Session cleared via onAuthStateChange');

          GmailService.clearCache();
          window.dispatchEvent(new CustomEvent('auth-state-changed', {
            detail: { event: _event, user: null }
          }));
        }
      }, 50); // 50ms debounce to prevent rapid firing
    });

    return () => {
      mounted = false;
      if (loadingTimeout) {
clearTimeout(loadingTimeout);
}
      if (authStateDebounceRef.current) {
        clearTimeout(authStateDebounceRef.current);
      }
      subscription.unsubscribe();
    };
  }, [clearClientAuthState, loadCompleteUserProfile, shouldProcessEvent]);

  // Proactive token refresh - keeps session alive during idle time
  useEffect(() => {
    if (!session || !isSupabaseConfigured()) {
return;
}

    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let isRefreshing = false;

    const scheduleTokenRefresh = () => {
      // Clear any existing timer
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }

      const expiresAt = session.expires_at;
      if (!expiresAt) {
return;
}

      const now = Math.floor(Date.now() / 1000);
      const expiresInSeconds = expiresAt - now;

      // Skip if token is already expired or about to expire
      if (expiresInSeconds <= 0) {
        log.debug('Token already expired, triggering refresh');
        refreshToken();
        return;
      }

      // Refresh at 75% of token lifetime, or 5 minutes before expiry (whichever is sooner)
      const refreshAtPercent = expiresInSeconds * 0.75;
      const refreshBefore5Min = expiresInSeconds - 300; // 5 minutes before expiry
      const refreshInSeconds = Math.min(refreshAtPercent, Math.max(refreshBefore5Min, 60));
      const refreshInMs = refreshInSeconds * 1000;

      log.debug('Scheduling proactive token refresh', {
        expiresInSeconds,
        refreshInSeconds: Math.round(refreshInSeconds),
        refreshInMinutes: Math.round(refreshInSeconds / 60)
      });

      refreshTimer = setTimeout(() => {
        refreshToken();
      }, refreshInMs);
    };

    const refreshToken = async () => {
      if (isRefreshing) {
return;
}
      isRefreshing = true;

      try {
        log.debug('Proactively refreshing session token');
        const { data, error } = await supabase.auth.refreshSession();

        if (!error && data.session) {
          log.debug('Session token refreshed successfully');
          setSession(data.session);
          // Note: scheduleTokenRefresh will be called again due to session dependency
        } else if (error) {
          log.warn('Failed to refresh session token', { error: error.message });
        }
      } catch (err) {
        log.error('Error during token refresh', undefined, err as Error);
      } finally {
        isRefreshing = false;
      }
    };

    // Handle visibility change (tab becomes active after being hidden)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        log.debug('Tab became visible, checking session');

        // Check if token is close to expiry or expired
        const expiresAt = session.expires_at;
        if (expiresAt) {
          const now = Math.floor(Date.now() / 1000);
          const expiresInSeconds = expiresAt - now;

          // If token expires in less than 5 minutes or already expired, refresh now
          if (expiresInSeconds < 300) {
            log.debug('Token expiring soon after tab visibility, refreshing');
            await refreshToken();
          } else {
            // Reschedule the refresh timer since we're back
            scheduleTokenRefresh();
          }
        }
      }
    };

    // Initial schedule
    scheduleTokenRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // Re-running on session identity change only clears and reschedules the
    // refresh timer, which is idempotent; identity is kept stable for no-op
    // auth events by isEquivalentSession, so this stays infrequent.
  }, [session]);

  const signInWithGoogle = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      // Development bypass for Google login
      const mockUser = {
        id: 'dev-user-123',
        email: 'anthony@lendwisemtg.com', // Dev mode always uses anthony as admin
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        email_confirmed_at: new Date().toISOString(),
        last_sign_in_at: new Date().toISOString(),
        role: 'authenticated',
        aud: 'authenticated',
        app_metadata: {},
        user_metadata: {
          provider: 'google',
          full_name: 'Anthony (Dev Mode)'
        }
      } as User;
      
      const mockSession = {
        access_token: 'mock-access-token',
        refresh_token: 'mock-refresh-token',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: mockUser
      } as Session;
      
      setUser(mockUser);
      setSession(mockSession);
      
      return { error: null };
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // This exact route is allowlisted in the live Supabase Auth config.
        // AuthContext captures provider tokens from the resulting SIGNED_IN
        // event and stores them through the authenticated Edge Function.
        redirectTo: `${window.location.origin}/auth/callback/cookie`,
        queryParams: {
          hd: 'lendwisemtg.com', // Restrict to your Google Workspace domain
          prompt: 'consent', // Force consent to ensure we get refresh token
          access_type: 'offline', // Request refresh token for long-term access
          include_granted_scopes: 'true' // Include previously granted scopes
        },
        scopes: 'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.metadata https://www.googleapis.com/auth/gmail.settings.basic',
        // Ensure we request offline access for refresh tokens
        skipBrowserRedirect: false
      }
    });

    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    // Development override: dev@lendwisemtg.com logs in as anthony@lendwisemtg.com
    const isDevelopmentLogin = email === 'dev@lendwisemtg.com';
    let actualEmail = email;
    
    if (isDevelopmentLogin) {
      log.debug('Dev login detected, switching to anthony@lendwisemtg.com');
      actualEmail = 'anthony@lendwisemtg.com';
    }

    // Development bypass - simulate successful login
    if (!isSupabaseConfigured() || isDevelopmentLogin) {
      // For dev login, we need to check if anthony exists in the database
      if (isDevelopmentLogin && isSupabaseConfigured()) {
        try {
          // Check if anthony@lendwisemtg.com exists in users
          const { data: existingProfile, error: profileError } = await supabase
            .from('users')
            .select('*')
            .eq('email', 'anthony@lendwisemtg.com')
            .single();

          if (existingProfile && !profileError) {
            // Create a session for the existing user
            const mockUser = {
              id: existingProfile.id,
              email: existingProfile.email,
              created_at: existingProfile.created_at,
              updated_at: existingProfile.updated_at,
              email_confirmed_at: new Date().toISOString(),
              last_sign_in_at: new Date().toISOString(),
              role: 'authenticated',
              aud: 'authenticated',
              app_metadata: { provider: 'email', providers: ['email'] },
              user_metadata: { 
                full_name: existingProfile.full_name || 'Anthony',
                email: existingProfile.email
              }
            } as User;
            
            const mockSession = {
              access_token: 'dev-access-token',
              refresh_token: 'dev-refresh-token',
              expires_in: 3600,
              expires_at: Math.floor(Date.now() / 1000) + 3600,
              token_type: 'bearer',
              user: mockUser
            } as Session;
            
            setUser(mockUser);
            setSession(mockSession);
            log.debug('Dev login successful - using existing anthony@lendwisemtg.com account');
            
            return { error: null };
          } else {
            log.error('anthony@lendwisemtg.com not found in users', undefined, profileError);
            return { error: { message: 'Admin account not found. Please ensure anthony@lendwisemtg.com exists in the database.' } };
          }
        } catch (err) {
          log.error('Error during dev login', undefined, err as Error);
          return { error: { message: 'Dev login failed' } };
        }
      }
      
      // Original mock user for non-configured Supabase
      const mockUser = {
        id: 'dev-user-123',
        email: actualEmail || 'dev@example.com',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        email_confirmed_at: new Date().toISOString(),
        last_sign_in_at: new Date().toISOString(),
        role: 'authenticated',
        aud: 'authenticated',
        app_metadata: {},
        user_metadata: {}
      } as User;
      
      const mockSession = {
        access_token: 'mock-access-token',
        refresh_token: 'mock-refresh-token',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: mockUser
      } as Session;
      
      setUser(mockUser);
      setSession(mockSession);
      
      return { error: null };
    }
    
    // Normal login flow for non-dev users
    const { error } = await supabase.auth.signInWithPassword({
      email: actualEmail,
      password
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    if (signOutInFlightRef.current) {
      return signOutInFlightRef.current;
    }

    log.info('Clearing user caches on logout');
    authStateSequenceRef.current += 1;
    clearClientAuthState();

    const operation = (async () => {
      try {
        if (!isSupabaseConfigured()) {
          return;
        }

        try {
          const { error } = await supabase.auth.signOut();
          if (error) {
            throw error;
          }
        } catch (error) {
          // A failed global revocation leaves auth-js storage intact. Remove
          // the local session cache so a reload cannot silently sign back in.
          clearSupabaseBrowserStorage();
          log.error('Supabase sign-out failed', undefined, error as Error);
          throw error;
        }
      } finally {
        // The app remains locally signed out even if remote revocation fails.
        initializeSecurityCleanup();
      }
    })();

    signOutInFlightRef.current = operation;
    try {
      await operation;
    } finally {
      if (signOutInFlightRef.current === operation) {
        signOutInFlightRef.current = null;
      }
    }
  }, [clearClientAuthState]);

  const refreshProfile = useCallback(async () => {
    if (!isSupabaseConfigured() || !user) {
      return;
    }

    log.debug('Refreshing user profile');

    try {
      // CRITICAL: Clear cache to force fresh fetch
      profileCheckCache.current.delete(user.id);

      // Get fresh session
      const { data: { session: freshSession } } = await supabase.auth.getSession();

      if (freshSession?.user) {
        // SINGLE QUERY: Use consolidated function that fetches all fields
        const extendedUser = await loadCompleteUserProfile(freshSession.user);

        if (!extendedUser) {
          clearClientAuthState();
          return;
        }

        // Update state
        setSession(freshSession);
        setUser(extendedUser);

        log.info('Profile refreshed successfully', {
          userId: extendedUser.id,
          roles: (extendedUser as ExtendedUser).roles,
          employmentStatus: (extendedUser as ExtendedUser).employment_status
        });
      } else {
        log.warn('No session found during profile refresh');
      }
    } catch (error) {
      log.error('Error refreshing profile', { userId: user.id }, error as Error);
    }
  }, [user, loadCompleteUserProfile, clearClientAuthState]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signIn,
    signInWithGoogle,
    signOut,
    refreshProfile,
    supabaseClient: supabase // Provide the authenticated client
  }), [user, session, loading, signIn, signInWithGoogle, signOut, refreshProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
